import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosApi from "src/app/config";

const initialState = {
  // Vehicles list
  vehicles: [] as any[],
  vehiclesLoading: false,
  vehiclesError: null as any,
  totalVehicles: 0,
  
  // Vehicle details
  selectedVehicle: null as any,
  vehicleDetailsLoading: false,
  vehicleDetailsError: null as any,
  
  // Client-specific vehicles
  clientVehicles: [] as any[],
  clientVehiclesLoading: false,
  clientVehiclesError: null as any,
  
  // Client vehicle operations
  attachingVehicle: false,
  detachingVehicle: false,
  creatingVehicleForClient: false,
  vehicleOperationError: null as any,
  
  // General
  isLoading: false,
  isError: false,
  error: null as any,
};


// Get vehicles by attachment status (attached/unattached to clients)
export const getVehiclesByAttachmentStatus = createAsyncThunk(
  "vehicles/getVehiclesByAttachmentStatus",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, attachmentFilter = 'all', page = 1, limit = 100, search } = params;
      
      const response = await axiosApi({ token }).post(`/vehicles/list-trips-vehicle`, {
        organizationId,
        offset: (page - 1) * limit,
        limit,
        search,
        attachmentFilter // 'all', 'attached', 'unattached'
      });
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Get vehicle by ID
export const getVehicleById = createAsyncThunk(
  "vehicles/getVehicleById",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, vehicleId } = params;
      
      const response = await axiosApi({ token }).get(`/vehicles/${vehicleId}`);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create vehicle
export const createVehicle = createAsyncThunk(
  "vehicles/createVehicle",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...vehicleData } = params;

      // organizationId is now handled by the backend from authentication
      const response = await axiosApi({ token, organizationId }).post(`/vehicles/create`, vehicleData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Update vehicle
export const updateVehicle = createAsyncThunk(
  "vehicles/updateVehicle",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, ...vehicleData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/vehicles/update`, vehicleData);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete vehicle
export const deleteVehicle = createAsyncThunk(
  "vehicles/deleteVehicle",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, vehicleId } = params;

      await axiosApi({ token }).delete(`/vehicles/${vehicleId}`);

      return { vehicleId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Attach a vehicle to a client
export const attachVehicleToClient = createAsyncThunk(
  "vehicles/attachVehicleToClient",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, vehicleId, clientId } = params;
      
      const response = await axiosApi({ token }).post(`/vehicles/attach/${vehicleId}/client/${clientId}`);
      
      return { ...response.data, vehicleId, clientId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Detach a vehicle from a client
export const detachVehicleFromClient = createAsyncThunk(
  "vehicles/detachVehicleFromClient",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, vehicleId } = params;
      
      const response = await axiosApi({ token }).post(`/vehicles/detach/${vehicleId}`);
      
      return { ...response.data, vehicleId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Create a new vehicle for a client
export const createVehicleForClient = createAsyncThunk(
  "vehicles/createVehicleForClient",
  async (params: any, { rejectWithValue }) => {
    try {
      const { token, organizationId, clientOrganizationId, ...vehicleData } = params;
      
      const response = await axiosApi({ token, organizationId }).post(`/vehicles/create-for-client/${clientOrganizationId}`, vehicleData);
      
      return { ...response.data, clientOrganizationId };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const vehiclesSlice = createSlice({
  name: "vehicles",
  initialState,
  reducers: {
    clearVehicleErrors: (state) => {
      state.vehiclesError = null;
      state.vehicleDetailsError = null;
      state.clientVehiclesError = null;
      state.vehicleOperationError = null;
      state.error = null;
      state.isError = false;
    },
    resetVehicleState: (state) => {
      return initialState;
    },
    setSelectedVehicle: (state, action) => {
      state.selectedVehicle = action.payload;
    },
    updateVehicleLocally: (state, action) => {
      const { vehicleId, updates } = action.payload;
      const vehicleIndex = state.vehicles.findIndex((v: any) => v.id === vehicleId);
      if (vehicleIndex !== -1) {
        state.vehicles[vehicleIndex] = { ...state.vehicles[vehicleIndex], ...updates };
      }
      if (state.selectedVehicle?.id === vehicleId) {
        state.selectedVehicle = { ...state.selectedVehicle, ...updates };
      }
    }
  },
  extraReducers: (builder) => {
    // Get vehicles by attachment status
    builder
      .addCase(getVehiclesByAttachmentStatus.pending, (state) => {
        state.vehiclesLoading = true;
        state.vehiclesError = null;
      })
      .addCase(getVehiclesByAttachmentStatus.fulfilled, (state, action) => {
        state.vehiclesLoading = false;
        state.vehicles = action.payload?.data || [];
        state.totalVehicles = action.payload?.data?.length || 0;
      })
      .addCase(getVehiclesByAttachmentStatus.rejected, (state, action) => {
        state.vehiclesLoading = false;
        state.vehiclesError = action.payload;
      });


    // Get vehicle by ID
    builder
      .addCase(getVehicleById.pending, (state) => {
        state.vehicleDetailsLoading = true;
        state.vehicleDetailsError = null;
      })
      .addCase(getVehicleById.fulfilled, (state, action) => {
        state.vehicleDetailsLoading = false;
        state.selectedVehicle = action.payload?.data;
      })
      .addCase(getVehicleById.rejected, (state, action) => {
        state.vehicleDetailsLoading = false;
        state.vehicleDetailsError = action.payload;
      });

    // Create vehicle
    builder
      .addCase(createVehicle.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVehicle.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          state.vehicles.unshift(action.payload.data);
          state.totalVehicles++;
        }
      })
      .addCase(createVehicle.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      });

    // Update vehicle
    builder
      .addCase(updateVehicle.fulfilled, (state, action) => {
        if (action.payload?.data) {
          const updatedVehicle = action.payload.data;
          const index = state.vehicles.findIndex((v: any) => v.id === updatedVehicle.id);
          if (index !== -1) {
            state.vehicles[index] = updatedVehicle;
          }
          if (state.selectedVehicle?.id === updatedVehicle.id) {
            state.selectedVehicle = updatedVehicle;
          }
        }
      });

    // Delete vehicle
    builder
      .addCase(deleteVehicle.fulfilled, (state, action) => {
        const { vehicleId } = action.payload;
        state.vehicles = state.vehicles.filter((v: any) => v.id !== vehicleId);
        state.totalVehicles--;
        if (state.selectedVehicle?.id === vehicleId) {
          state.selectedVehicle = null;
        }
      });

    // Attach vehicle to client
    builder
      .addCase(attachVehicleToClient.pending, (state) => {
        state.attachingVehicle = true;
        state.vehicleOperationError = null;
      })
      .addCase(attachVehicleToClient.fulfilled, (state, action) => {
        state.attachingVehicle = false;
        // Update the vehicle in the main list if it exists
        if (action.payload?.data) {
          const updatedVehicle = action.payload.data;
          const index = state.vehicles.findIndex((v: any) => v.id === updatedVehicle.id);
          if (index !== -1) {
            state.vehicles[index] = updatedVehicle;
          }
        }
      })
      .addCase(attachVehicleToClient.rejected, (state, action) => {
        state.attachingVehicle = false;
        state.vehicleOperationError = action.payload;
      });

    // Detach vehicle from client
    builder
      .addCase(detachVehicleFromClient.pending, (state) => {
        state.detachingVehicle = true;
        state.vehicleOperationError = null;
      })
      .addCase(detachVehicleFromClient.fulfilled, (state, action) => {
        state.detachingVehicle = false;
        // Update the vehicle in the main list if it exists
        if (action.payload?.data) {
          const updatedVehicle = action.payload.data;
          const index = state.vehicles.findIndex((v: any) => v.id === updatedVehicle.id);
          if (index !== -1) {
            state.vehicles[index] = updatedVehicle;
          }
        }
        // Remove from client vehicles list
        if (action.payload?.vehicleId) {
          state.clientVehicles = state.clientVehicles.filter((v: any) => v.id !== action.payload.vehicleId);
        }
      })
      .addCase(detachVehicleFromClient.rejected, (state, action) => {
        state.detachingVehicle = false;
        state.vehicleOperationError = action.payload;
      });

    // Create vehicle for client
    builder
      .addCase(createVehicleForClient.pending, (state) => {
        state.creatingVehicleForClient = true;
        state.vehicleOperationError = null;
      })
      .addCase(createVehicleForClient.fulfilled, (state, action) => {
        state.creatingVehicleForClient = false;
        if (action.payload?.data) {
          // Add to main vehicles list
          state.vehicles.unshift(action.payload.data);
          state.totalVehicles++;
          // Add to client vehicles list if we're viewing that client
          state.clientVehicles.unshift(action.payload.data);
        }
      })
      .addCase(createVehicleForClient.rejected, (state, action) => {
        state.creatingVehicleForClient = false;
        state.vehicleOperationError = action.payload;
      });
  },
});

export const {
  clearVehicleErrors,
  resetVehicleState,
  setSelectedVehicle,
  updateVehicleLocally
} = vehiclesSlice.actions;

export default vehiclesSlice.reducer;